/* ==========================================================================
   Lumistic — theme runtime
   No dependencies. Every behaviour is a custom element so markup declares its
   own interactivity and nothing runs on pages that don't use it.
   ========================================================================== */
(function () {
  'use strict';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const announce = (message) => {
    const region = document.getElementById('a11y-live');
    if (region) region.textContent = message;
  };

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    const fmt = window.Lumistic && window.Lumistic.moneyFormat;
    if (!fmt) return '$' + amount;
    // Handles amount, amount_with_comma_separator, amount_no_decimals, etc.
    // Some shops' money_format wraps the value in markup — strip it.
    return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  };


  /* ------------------------------------------------------------------------
     Toast
     Confirmation for actions whose result is off-screen. Deliberately quiet:
     one line, one thumbnail, no icon soup, and it never blocks the UI. Screen
     readers get the same message through the live region, so the toast is
     purely visual reinforcement.
     ---------------------------------------------------------------------- */
  const Toast = {
    root: null,

    mount() {
      if (this.root) return this.root;
      this.root = document.createElement('div');
      this.root.className = 'toast-stack';
      this.root.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.root);
      return this.root;
    },

    show({ title, meta, image }) {
      const root = this.mount();

      const el = document.createElement('div');
      el.className = 'toast';
      el.innerHTML = `
        ${image ? `<span class="toast__media"><img src="${image}" alt="" width="44" height="44"></span>` : ''}
        <span class="toast__body">
          <span class="toast__title">${title}</span>
          ${meta ? `<span class="toast__meta">${meta}</span>` : ''}
        </span>
      `;

      root.appendChild(el);
      requestAnimationFrame(() => el.classList.add('is-in'));

      const dismiss = () => {
        el.classList.remove('is-in');
        el.addEventListener('transitionend', () => el.remove(), { once: true });
        // Belt and braces: if the transition never fires (element hidden,
        // reduced motion), still clean up so toasts can't accumulate.
        setTimeout(() => el.remove(), 600);
      };

      setTimeout(dismiss, 3200);
      el.addEventListener('click', dismiss);
    }
  };

  /* ------------------------------------------------------------------------
     Scroll reveal
     One shared observer for the whole document. Elements unobserve on first
     reveal, so cost is bounded by element count, not scroll distance.
     ---------------------------------------------------------------------- */
  const Reveal = {
    observer: null,

    init() {
      if (!document.documentElement.classList.contains('js-reveal')) return;
      if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        document.documentElement.classList.remove('js-reveal');
        return;
      }

      // Reveals are two-way: elements fade out again once they leave the
      // viewport and fade back in on the way up. That means never unobserving,
      // so the class is toggled rather than added.
      //
      // The margin is asymmetric on purpose. Entering, an element must be 10%
      // inside the fold before it animates, so nothing fires while still
      // clipped at the edge. Leaving, it is allowed to travel a full viewport
      // past the boundary before it fades — without that slack, anything taller
      // than the screen would flicker at both ends as its own edges cross the
      // trigger line while the reader is still in the middle of it.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-revealed', entry.isIntersecting);
          });
        },
        { rootMargin: '100% 0px -10% 0px', threshold: 0 }
      );

      this.scan(document);
    },

    scan(root) {
      if (!this.observer) return;
      // Not :not(.is-revealed) any more. Reveals toggle rather than latch, so
      // a revealed element still needs observing. observe() is idempotent, so
      // re-scanning a section costs nothing.
      root.querySelectorAll('[data-reveal]').forEach((el, i) => {
        // Stagger only within a group, and cap it — a long stagger on a big
        // grid reads as lag, not craft.
        if (el.hasAttribute('data-reveal-stagger')) {
          const delay = Math.min(i * 70, 350);
          el.style.setProperty('--reveal-delay', delay + 'ms');
        }
        this.observer.observe(el);
      });
    }
  };

  /* ------------------------------------------------------------------------
     Header — condensed state on scroll
     ---------------------------------------------------------------------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.ticking = false;
      this.lastState = false;
      this.lastShown = null;
      // Opt the document in to the hide/reveal behaviour only now that this
      // element is live. Until this runs the header is a plain sticky bar, so
      // a failed or blocked script leaves navigation reachable.
      document.documentElement.classList.add('header-auto');
      // Set by Liquid on every template that does not open with a full-bleed
      // hero. Applied before the first measurement so the bar is never absent
      // for a frame on the pages that always want it.
      this.pinned = this.hasAttribute('data-pinned');
      if (this.pinned) {
        this.classList.add('is-shown');
        this.lastShown = true;
      }
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;

        // The bar is held back over the opening screen so the hero reads as a
        // full, uninterrupted frame, then slides down and stays for the rest
        // of the page. 62% of the viewport rather than a fixed pixel count:
        // the trigger has to track the fold, and the fold is a different place
        // on a phone than on a monitor.
        const trigger = window.innerHeight * 0.62;
        const shown = this.pinned || y > trigger;

        if (shown !== this.lastShown) {
          this.classList.toggle('is-shown', shown);
          this.lastShown = shown;
        }

        // Glass follows a much lower threshold so the pill is never a pane of
        // clear glass with dark text sitting on a dark photograph.
        const scrolled = y > 8;
        if (scrolled !== this.lastState) {
          this.classList.toggle('is-scrolled', scrolled);
          this.lastState = scrolled;
        }
        this.ticking = false;
      });
    }
  }

  /* ------------------------------------------------------------------------
     Drawer — shared by mobile nav and cart
     Focus trap, Escape to close, scroll lock, focus restoration.
     ---------------------------------------------------------------------- */
  class SiteDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.opener = null;
      this.onKeydown = this.onKeydown.bind(this);

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this) this.close();
      });

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest(`[data-drawer-open="${this.id}"]`);
        if (trigger) {
          e.preventDefault();
          this.open(trigger);
        }
      });
    }

    open(opener) {
      this.opener = opener || null;
      this.setAttribute('open', '');
      document.body.classList.add('no-scroll');
      document.addEventListener('keydown', this.onKeydown);

      requestAnimationFrame(() => {
        const target = this.querySelector('[data-drawer-initial-focus]') || this.panel;
        if (target) target.focus();
      });
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.body.classList.remove('no-scroll');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ------------------------------------------------------------------------
     Accordion
     Height is animated explicitly because `height: auto` is not animatable
     and `max-height` guesswork produces uneven easing.
     ---------------------------------------------------------------------- */
  class SiteAccordion extends HTMLElement {
    connectedCallback() {
      this.single = this.hasAttribute('data-single');
      this.items = Array.from(this.querySelectorAll('[data-accordion-item]'));

      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const panel = item.querySelector('[data-accordion-panel]');
        if (!trigger || !panel) return;

        panel.style.height = item.hasAttribute('data-open') ? 'auto' : '0px';
        trigger.setAttribute('aria-expanded', item.hasAttribute('data-open') ? 'true' : 'false');

        trigger.addEventListener('click', () => this.toggle(item));
      });
    }

    toggle(item) {
      const isOpen = item.hasAttribute('data-open');

      if (this.single && !isOpen) {
        this.items.forEach((other) => {
          if (other !== item && other.hasAttribute('data-open')) this.collapse(other);
        });
      }

      isOpen ? this.collapse(item) : this.expand(item);
    }

    expand(item) {
      const panel = item.querySelector('[data-accordion-panel]');
      const trigger = item.querySelector('[data-accordion-trigger]');
      item.setAttribute('data-open', '');
      trigger.setAttribute('aria-expanded', 'true');

      if (prefersReducedMotion()) {
        panel.style.height = 'auto';
        return;
      }

      const target = panel.scrollHeight;
      panel.style.height = '0px';
      requestAnimationFrame(() => {
        panel.style.height = target + 'px';
        panel.addEventListener(
          'transitionend',
          () => { if (item.hasAttribute('data-open')) panel.style.height = 'auto'; },
          { once: true }
        );
      });
    }

    collapse(item) {
      const panel = item.querySelector('[data-accordion-panel]');
      const trigger = item.querySelector('[data-accordion-trigger]');
      item.removeAttribute('data-open');
      trigger.setAttribute('aria-expanded', 'false');

      if (prefersReducedMotion()) {
        panel.style.height = '0px';
        return;
      }

      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(() => { panel.style.height = '0px'; });
    }
  }

  /* ------------------------------------------------------------------------
     Product gallery
     ---------------------------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-gallery-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.select(Number(thumb.dataset.galleryThumb)));
      });

      // Keep thumbnails in sync when the stage is swiped on touch devices.
      if (this.stage) {
        let scrollTimer;
        this.stage.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const index = Math.round(this.stage.scrollLeft / this.stage.clientWidth);
            this.setActiveThumb(index);
          }, 90);
        }, { passive: true });
      }

      document.addEventListener('variant:change', (e) => {
        const mediaId = e.detail && e.detail.variant && e.detail.variant.featured_media
          ? e.detail.variant.featured_media.id
          : null;
        if (!mediaId) return;
        const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (index > -1) this.select(index);
      });
    }

    select(index) {
      const slide = this.slides[index];
      if (!slide || !this.stage) return;
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';

      // The stage is a swipeable row on small screens and a stacked column on
      // desktop. Detect which by asking whether it actually overflows
      // horizontally, rather than duplicating the breakpoint in JS where it
      // would drift out of step with the stylesheet.
      if (this.stage.scrollWidth > this.stage.clientWidth + 1) {
        this.stage.scrollTo({ left: slide.offsetLeft - this.stage.offsetLeft, behavior });
      } else {
        slide.scrollIntoView({ behavior, block: 'center' });
      }

      this.setActiveThumb(index);
    }

    setActiveThumb(index) {
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }
  }

  /* ------------------------------------------------------------------------
     Variant selection — drives price, availability and the bundle cards
     ---------------------------------------------------------------------- */
  class VariantSelector extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.idInput = this.querySelector('[data-variant-id]');
      this.qtyInput = this.querySelector('[data-variant-qty]');
      // Every bundle adds this variant; only the count changes. See the note in
      // main-product.liquid for why the bundle variants are not what is sold.
      this.baseId = this.dataset.baseVariantId || '';

      try {
        this.variants = JSON.parse(this.querySelector('[data-variant-data]').textContent);
      } catch (e) {
        this.variants = [];
        return;
      }

      this.querySelectorAll('[data-variant-option]').forEach((input) => {
        input.addEventListener('change', () => this.onChange(input));
      });

      const initial = this.variants.find((v) => String(v.id) === String(this.idInput.value));
      if (initial) {
        this.render(initial);
        // A ?variant= link can land on a bundle, so the submitted values are
        // mapped on load too rather than only on the first click.
        const checked = this.querySelector('[data-variant-option]:checked');
        this.applySelection(checked ? checked.dataset.units : null);
      }
    }

    /* What the form actually posts: always the single-unit variant, with the
       chosen bundle expressed as a quantity. Falls back to the plain variant id
       when there is no base (a single-variant product), so this stays correct
       for any other product using this section. */
    applySelection(units) {
      const n = Math.max(1, parseInt(units, 10) || 1);
      if (this.baseId) this.idInput.value = this.baseId;
      if (this.qtyInput) this.qtyInput.value = n;
    }

    onChange(input) {
      const variant = this.variants.find((v) => String(v.id) === String(input.value));
      if (!variant) return;
      // Pricing, savings and the button label keep reading the bundle variant —
      // it is still the thing the customer chose and the price they expect.
      this.render(variant);
      this.applySelection(input.dataset.units);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }

    render(variant) {
      const root = document.querySelector('[data-product-root]') || document;

      root.querySelectorAll('[data-price-current]').forEach((el) => {
        el.textContent = formatMoney(variant.price);
      });

      root.querySelectorAll('[data-price-compare]').forEach((el) => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.textContent = formatMoney(variant.compare_at_price);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      root.querySelectorAll('[data-price-saving]').forEach((el) => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const pct = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          el.textContent = pct + '%';
          el.closest('[data-saving-wrap]')?.removeAttribute('hidden');
        } else {
          el.closest('[data-saving-wrap]')?.setAttribute('hidden', '');
        }
      });

      // Availability follows whatever is actually being added to the cart,
      // which since the bundle change is the single-unit variant rather than
      // the card the customer clicked. The bundle variants are untracked and so
      // report available forever; trusting them would leave the button live
      // with the real variant sold out, and the failure would surface as an
      // error at the cart instead of a disabled button on the page.
      const sellable = this.variants.find((v) => String(v.id) === String(this.baseId)) || variant;

      root.querySelectorAll('[data-atc-button]').forEach((btn) => {
        const label = btn.querySelector('[data-atc-label]');
        btn.disabled = !sellable.available;
        if (label) {
          label.textContent = sellable.available
            ? btn.dataset.labelAvailable || 'Add to bag'
            : btn.dataset.labelSoldOut || 'Sold out';
        }
      });

      root.querySelectorAll('[data-variant-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.variantCard === String(variant.id));
      });
    }
  }

  /* ------------------------------------------------------------------------
     Add to cart
     ---------------------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[data-atc-button]');
      this.errorEl = this.querySelector('[data-atc-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled) return;

      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      // No `sections` param — nothing on this page renders the cart. The only
      // thing that needs updating after an add is the header count, which
      // refreshCount() reads straight from /cart.js.
      const formData = new FormData(this.form);

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Could not add to cart.');
        }

        announce('Added to your bag.');
        this.flashSuccess();
        this.refreshCount();

        // "Default Title" is Shopify's placeholder for single-variant products
        // and must never reach the customer.
        //
        // A leading digit means the title is a unit count rather than a choice
        // ("1 device"). Every bundle adds the single-unit variant with a
        // quantity, so that label always reads "1 device" — the toast for a
        // two-pack said "Heated Eye Massager · 1 device", which is the opposite
        // of what just happened. Real variant names do not start with a number.
        const variantLabel =
          data.variant_title &&
          !/default/i.test(data.variant_title) &&
          !/^\s*\d/.test(data.variant_title)
            ? data.variant_title
            : null;

        Toast.show({
          title: 'Added to your bag',
          meta: [data.product_title, variantLabel].filter(Boolean).join(' · '),
          image: data.image ? data.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : null
        });

        // Adding to the bag does not interrupt the customer: no drawer, no
        // redirect. The tick, the toast and the header count are the
        // confirmation. The bag itself lives at /cart, reachable from the
        // header whenever they choose to go there.
      } catch (error) {
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        }
        announce(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
      const spinner = this.button.querySelector('[data-atc-spinner]');
      const label = this.button.querySelector('[data-atc-label]');
      if (spinner) spinner.hidden = !loading;
      if (label) label.style.opacity = loading ? '0.5' : '1';
    }

    async refreshCount() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js', {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;
        const cart = await response.json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      } catch (e) {
        // Non-fatal: the item is in the bag and the toast already said so.
      }
    }

    // Brief confirmation on the button itself, so the eye gets an answer at
    // the point of interaction rather than only in the drawer. The tick is
    // drawn by CSS off the .is-added class — nothing to sequence in JS.
    flashSuccess() {
      if (!this.button) return;
      const label = this.button.querySelector('[data-atc-label]');
      if (!label) return;

      // Guard against overlapping presses restoring the wrong label.
      if (this.successTimer) {
        clearTimeout(this.successTimer);
      } else {
        this.originalLabel = label.textContent;
      }

      this.button.classList.add('is-added');
      label.textContent = 'Added to bag';

      this.successTimer = setTimeout(() => {
        this.button.classList.remove('is-added');
        label.textContent = this.originalLabel;
        this.successTimer = null;
      }, 1800);
    }
  }

  /* ------------------------------------------------------------------------
     Cart drawer contents — re-rendered from section rendering API
     ---------------------------------------------------------------------- */

  /* ------------------------------------------------------------------------
     Sticky add-to-cart bar — appears once the primary buy block scrolls away
     ---------------------------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const sentinel = document.querySelector('[data-atc-sentinel]');
      if (!sentinel || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          // Show only when the buy block has scrolled off the TOP of the
          // viewport, not when it is simply out of view further down the page.
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', past);
          this.setAttribute('aria-hidden', past ? 'false' : 'true');
        },
        { threshold: 0 }
      );
      this.observer.observe(sentinel);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ------------------------------------------------------------------------
     FAQ search
     ---------------------------------------------------------------------- */
  class FaqSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-faq-input]');
      this.empty = document.querySelector('[data-faq-empty]');
      this.groups = Array.from(document.querySelectorAll('[data-faq-group]'));
      this.items = Array.from(document.querySelectorAll('[data-faq-item]'));
      if (!this.input) return;

      let debounce;
      this.input.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => this.filter(this.input.value.trim().toLowerCase()), 130);
      });
    }

    filter(term) {
      let visible = 0;

      this.items.forEach((item) => {
        const match = !term || (item.dataset.faqText || '').includes(term);
        item.hidden = !match;
        if (match) visible++;
      });

      this.groups.forEach((group) => {
        const anyVisible = group.querySelectorAll('[data-faq-item]:not([hidden])').length > 0;
        group.hidden = !anyVisible;
      });

      if (this.empty) this.empty.hidden = visible > 0;
      announce(visible + ' questions match.');
    }
  }

  /* ------------------------------------------------------------------------
     Order tracking
     Shopify has no public storefront endpoint for looking up an order by
     email + number without authentication, so this routes the customer to the
     authenticated account order page rather than pretending to query an API.
     ---------------------------------------------------------------------- */
  class OrderTracker extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-track-status]');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    onSubmit(event) {
      event.preventDefault();
      const orderInput = this.form.querySelector('[name="order_number"]');
      const emailInput = this.form.querySelector('[name="email"]');

      const orderNumber = (orderInput.value || '').trim().replace(/^#/, '');
      const email = (emailInput.value || '').trim();

      let error = '';
      if (!orderNumber) error = 'Enter your order number.';
      else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) error = 'Enter the email address used on the order.';

      if (error) {
        this.showStatus(error, 'error');
        (orderNumber ? emailInput : orderInput).focus();
        return;
      }

      this.showStatus('Taking you to your order…', 'success');
      const base = this.dataset.trackingUrl || (window.Shopify.routes.root + 'account/login');
      const url = new URL(base, window.location.origin);
      url.searchParams.set('order', orderNumber);
      url.searchParams.set('email', email);
      window.location.href = url.toString();
    }

    showStatus(message, kind) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.className = 'form-status form-status--' + kind;
      this.status.hidden = false;
      announce(message);
    }
  }

  /* ------------------------------------------------------------------------
     Quantity stepper
     ---------------------------------------------------------------------- */

  /* ------------------------------------------------------------------------
     Line-split headlines
     Wraps each rendered line of a heading in a masked row so the type rises
     out of its own baseline. Runs after fonts settle — splitting against the
     fallback metrics would group the words into the wrong lines and the
     headline would re-flow visibly once the display face arrived.
     Text nodes are built with the DOM, never innerHTML: the source is
     merchant-authored and must not be re-parsed as markup.
     ---------------------------------------------------------------------- */
  const SplitLines = {
    targets: [],

    init() {
      // Splitting is only safe when the reveal system is actually running:
      // a split line sits at translateY(105%) inside an overflow-hidden row
      // and is invisible until something adds `is-revealed`. If reveals are
      // switched off in theme settings, or IntersectionObserver is missing,
      // nothing would ever add it and the headline would vanish. Leaving the
      // heading unsplit is the correct fallback.
      if (prefersReducedMotion()) return;
      if (!document.documentElement.classList.contains('js-reveal')) return;
      if (!Reveal.observer) return;

      this.targets = Array.from(document.querySelectorAll('[data-split-lines]'));
      if (!this.targets.length) return;

      const run = () => this.targets.forEach((el) => this.split(el));

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(run).catch(run);
      } else {
        run();
      }

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(run, 180);
      }, { passive: true });
    },

    split(el) {
      // Keep the pristine string so repeated splits never compound.
      if (!el.dataset.splitSource) el.dataset.splitSource = el.textContent.trim();
      const source = el.dataset.splitSource;
      if (!source) return;

      const words = source.split(/\s+/);
      const probes = [];

      el.textContent = '';
      words.forEach((word, i) => {
        const span = document.createElement('span');
        span.style.display = 'inline-block';
        span.textContent = word;
        el.appendChild(span);
        probes.push(span);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });

      // Group words into lines by their rendered vertical offset.
      const lines = [];
      let currentTop = null;
      probes.forEach((span) => {
        const top = span.offsetTop;
        if (currentTop === null || Math.abs(top - currentTop) > 2) {
          currentTop = top;
          lines.push([]);
        }
        lines[lines.length - 1].push(span.textContent);
      });

      el.textContent = '';
      el.classList.add('reveal-lines');

      lines.forEach((line, i) => {
        const row = document.createElement('span');
        row.className = 'reveal-lines__line';
        const inner = document.createElement('span');
        inner.className = 'reveal-lines__inner';
        inner.textContent = line.join(' ');
        // Later lines trail the ones above them, so the headline reads
        // top-down rather than arriving as one slab.
        inner.style.setProperty('--reveal-delay', i * 85 + 'ms');
        row.appendChild(inner);
        el.appendChild(row);
      });

      // The element may already be on screen; reveal immediately in that case
      // so a split headline above the fold is never left hidden.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) {
        requestAnimationFrame(() => el.classList.add('is-revealed'));
      } else if (Reveal.observer) {
        Reveal.observer.observe(el);
      }
    }
  };

  /* ------------------------------------------------------------------------
     Parallax
     Writes a CSS variable rather than setting transform directly, so the
     element keeps whatever transform its stylesheet declares and the work
     stays on the compositor. Only runs while the element is on screen.
     ---------------------------------------------------------------------- */
  const Parallax = {
    items: [],
    ticking: false,

    init() {
      if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;
      const nodes = Array.from(document.querySelectorAll('[data-parallax]'));
      if (!nodes.length) return;

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const node = entry.target;
          if (entry.isIntersecting) {
            if (!this.items.includes(node)) this.items.push(node);
          } else {
            this.items = this.items.filter((n) => n !== node);
          }
        });
        this.request();
      }, { rootMargin: '10% 0px' });

      nodes.forEach((n) => io.observe(n));

      this.onScroll = () => this.request();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    },

    request() {
      if (this.ticking || !this.items.length) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    },

    update() {
      const vh = window.innerHeight;
      this.items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // -1 when the element is entering from below, +1 when leaving above.
        const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        const strength = parseFloat(el.dataset.parallax) || 26;
        el.style.setProperty('--parallax', (progress * strength).toFixed(2) + 'px');
      });
    }
  };

  /* ------------------------------------------------------------------------
     Spotlight
     A single delegated listener for every card on the page. Coordinates are
     written as percentages so the gradient tracks correctly at any card size.
     ---------------------------------------------------------------------- */
  const Spotlight = {
    init() {
      if (prefersReducedMotion()) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      let frame = null;
      let pending = null;

      document.addEventListener('pointermove', (event) => {
        const card = event.target.closest('[data-spotlight]');
        if (!card) return;
        pending = { card, x: event.clientX, y: event.clientY };
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          if (!pending) return;
          const { card: el, x, y } = pending;
          const rect = el.getBoundingClientRect();
          el.style.setProperty('--mx', (((x - rect.left) / rect.width) * 100).toFixed(1) + '%');
          el.style.setProperty('--my', (((y - rect.top) / rect.height) * 100).toFixed(1) + '%');
        });
      }, { passive: true });
    }
  };

  /* ------------------------------------------------------------------------
     Header contrast
     The header is a light glass pill. Over an ink block that glass resolves to
     a flat grey slab with dark text on it — legible, but it looks like a
     rendering artefact rather than a decision. This flips the pill to dark
     glass while it overlaps an inverted block.

     Deliberately a scroll handler and not an IntersectionObserver: expressing
     "does this element overlap a band the height of the header" as a
     rootMargin means recomputing that margin on every resize and on every
     header height change, for no gain. There are one or two dark blocks per
     page, so a rect comparison inside rAF is both cheaper and exact.
     ---------------------------------------------------------------------- */
  const HeaderTheme = {
    init() {
      this.header = document.querySelector('site-header');
      if (!this.header) return;

      this.darks = Array.from(document.querySelectorAll('.block--inverse, .footer'));
      if (!this.darks.length) return;

      this.ticking = false;
      this.last = null;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    },

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    },

    update() {
      const bar = this.header.getBoundingClientRect();
      // Test the middle of the pill, not its edges: a block whose corner just
      // clips the header should not flip the whole bar.
      const probe = bar.top + bar.height / 2;

      const over = this.darks.some((el) => {
        const r = el.getBoundingClientRect();
        return r.top <= probe && r.bottom >= probe;
      });

      if (over !== this.last) {
        this.header.classList.toggle('is-over-dark', over);
        this.last = over;
      }
    }
  };

  /* ------------------------------------------------------------------------
     Background video
     A looping hero video that keeps decoding while it is scrolled out of view
     burns battery on a phone for something nobody can see. This starts it only
     once it is on screen and pauses it the moment it leaves.

     preload is "none" in the markup, so the file is not fetched at all until
     the element is first intersected — the poster carries the hero until then,
     which keeps the video off the critical path for first paint.
     ---------------------------------------------------------------------- */
  const BgVideo = {
    init() {
      const vids = Array.from(document.querySelectorAll('video[data-autopause]'));
      if (!vids.length) return;

      if (prefersReducedMotion()) {
        vids.forEach((v) => { v.pause(); v.removeAttribute('autoplay'); });
        return;
      }

      if (!('IntersectionObserver' in window)) return;

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            if (v.preload === 'none') v.preload = 'auto';
            // play() rejects on some browsers until the user has interacted.
            // The poster is already showing, so a rejection is not a failure.
            const played = v.play();
            if (played && played.catch) played.catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { rootMargin: '200px 0px' });

      vids.forEach((v) => io.observe(v));
    }
  };

  /* ------------------------------------------------------------------------
     Marquee
     Duplicates the track so the loop has no seam. The clone is hidden from
     assistive technology, so the strip is announced once.
     ---------------------------------------------------------------------- */
  const Marquee = {
    init(root) {
      (root || document).querySelectorAll('[data-marquee]:not([data-marquee-ready])').forEach((el) => {
        const track = el.querySelector('[data-marquee-track]');
        if (!track) return;
        el.setAttribute('data-marquee-ready', '');
        const clone = track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-marquee-track');
        el.appendChild(clone);
      });
    }
  };

  /* ------------------------------------------------------------------------
     Morph media
     Pairs the product image either side of a navigation so the View
     Transitions API animates one object moving rather than two pages
     cross-fading. The CSS half lives in base.css section 13.

     This exists in JavaScript for one reason: a view-transition-name must be
     unique within the document. A collection page holds a dozen product
     images and only the one actually being opened may carry the name — two
     elements sharing it makes the browser abandon the whole transition. So
     the name is moved onto whichever image is leaving, at the moment it is
     clicked, and never declared statically in the stylesheet.

     Nothing here is load-bearing. In a browser without view transitions the
     class is inert and navigation is ordinary.
     ---------------------------------------------------------------------- */
  const MorphMedia = {
    CLASS: 'vt-product-media',

    init() {
      // Skipped entirely under reduced motion: the transition is suppressed in
      // CSS, so assigning names would only add work with nothing to show.
      if (prefersReducedMotion()) return;

      this.claimGallery();

      // Capture phase. The class must be on the element before the browser
      // snapshots the outgoing page, and that snapshot is taken as soon as the
      // click's default action starts the navigation.
      document.addEventListener(
        'click',
        (event) => {
          // Modified and non-primary clicks open a new tab or window — this
          // page is not leaving, so moving the name off the gallery would
          // strand it on an element that never transitions.
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) return;

          const link = event.target.closest('a[href*="/products/"]');
          if (!link || link.target === '_blank') return;

          // The card's title and its image are separate links; either should
          // hand over the same image, so resolve from the card when there is
          // one and fall back to the link itself.
          const card = link.closest('article') || link;
          this.claim(card.querySelector('img') || link.querySelector('img'));
        },
        { capture: true }
      );

      // A page restored from bfcache keeps whatever was tagged when it was
      // left. On a product page that is a related-product card, which would
      // then collide with the gallery image on the next navigation away.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.claimGallery();
      });
    },

    claimGallery() {
      this.claim(document.querySelector('.gallery__slide img'));
    },

    claim(img) {
      this.release();
      if (img) img.classList.add(this.CLASS);
    },

    release() {
      document
        .querySelectorAll('.' + this.CLASS)
        .forEach((el) => el.classList.remove(this.CLASS));
    }
  };

  /* ------------------------------------------------------------------------
     Register + boot
     ---------------------------------------------------------------------- */
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  define('site-header', SiteHeader);
  define('site-drawer', SiteDrawer);
  define('site-accordion', SiteAccordion);
  define('product-gallery', ProductGallery);
  define('variant-selector', VariantSelector);
  define('product-form', ProductForm);
  define('sticky-atc', StickyAtc);
  define('faq-search', FaqSearch);
  define('order-tracker', OrderTracker);


  /* ------------------------------------------------------------------------
     Walkaround
     A 360° turn driven by scroll position. The frames are pre-rendered stills
     painted to a canvas rather than a video, because a <video> cannot be
     scrubbed backwards smoothly and this has to rewind exactly when the
     customer scrolls back up.

     Nothing downloads until the section is near the viewport: the sequence is
     the heaviest thing on the page and most visitors never reach it.
     ---------------------------------------------------------------------- */
  class LumWalkaround extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.walkaround__stage');
      this.canvas = this.querySelector('[data-canvas]');
      this.fallback = this.querySelector('[data-fallback]');
      if (!this.canvas || !this.stage) return;

      // Reduced motion, or no canvas: the written list is already in the
      // markup and says the same things. Leave it and do nothing else.
      if (prefersReducedMotion() || !this.canvas.getContext) return;

      this.count = parseInt(this.dataset.count, 10) || 96;
      this.urls = this.readFrameUrls();
      this.spots = this.readJSON('[data-hotspots]') || [];
      if (!this.urls.length) return;

      this.ctx = this.canvas.getContext('2d', { alpha: false });
      this.frames = [];
      this.loaded = 0;
      this.painted = -1;
      this.target = 0;
      this.eased = 0;
      this.box = { x: 0, y: 0, w: 0, h: 0 };
      this.activeId = null;
      this.running = false;

      this.el = {
        loading: this.querySelector('[data-loading]'),
        loadingValue: this.querySelector('[data-loading-value]'),
        degrees: this.querySelector('[data-degrees]'),
        railFill: this.querySelector('[data-rail-fill]'),
        rail: this.querySelector('[data-rail]'),
        hint: this.querySelector('[data-hint]'),
        marker: this.querySelector('[data-marker]'),
        leader: this.querySelector('[data-leader]'),
        leaderLine: this.querySelector('[data-leader-line]'),
        callout: this.querySelector('[data-callout]'),
        cIndex: this.querySelector('[data-callout-index]'),
        cTitle: this.querySelector('[data-callout-title]'),
        cBody: this.querySelector('[data-callout-body]'),
        cSpec: this.querySelector('[data-callout-spec]')
      };

      this.paintBg = getComputedStyle(this).getPropertyValue('--walkaround-bg').trim() || '#f6f9fd';

      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);

      this.buildRailTicks();

      // Only start fetching once the turn is plausibly about to be seen.
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
              this.io.disconnect();
              this.io = null;
              this.preload();
            }
          },
          { rootMargin: '150% 0px' }
        );
        this.io.observe(this);
      } else {
        this.preload();
      }
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.io) this.io.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.frames = [];
    }

    readJSON(selector) {
      const node = this.querySelector(selector);
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (err) { return null; }
    }

    readFrameUrls() {
      const wide = window.matchMedia('(min-width: 750px)').matches;
      const hi = this.readJSON('[data-frames-hi]') || [];
      const lo = this.readJSON('[data-frames-lo]') || [];
      const chosen = wide ? hi : lo;
      return (chosen.length ? chosen : hi).filter(Boolean);
    }

    preload() {
      const total = this.urls.length;
      this.urls.forEach((url, i) => {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        img.onload = img.onerror = () => {
          this.loaded += 1;
          const pct = Math.round((this.loaded / total) * 100);
          if (this.el.loadingValue) this.el.loadingValue.textContent = pct + '%';
          if (this.loaded === 1) this.start();
          if (this.loaded === total && this.el.loading) {
            this.el.loading.setAttribute('hidden', '');
          }
        };
        this.frames[i] = img;
      });
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.classList.add('is-live');
      if (this.fallback) this.fallback.setAttribute('hidden', '');
      window.addEventListener('resize', this.onResize, { passive: true });
      this.raf = requestAnimationFrame(this.tick);
    }

    onResize() {
      this.painted = -1;
      this.measureCallout();
    }

    buildRailTicks() {
      if (!this.el.rail || !this.spots.length) return;
      this.ticks = this.spots.map((spot) => {
        const tick = document.createElement('span');
        tick.className = 'walkaround__rail-tick';
        tick.style.left = (spot.frame / (this.count - 1)) * 100 + '%';
        this.el.rail.appendChild(tick);
        return tick;
      });
    }

    /* Scroll position within the section, 0 → 1. */
    measure() {
      const rect = this.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return;
      this.target = Math.min(1, Math.max(0, -rect.top / scrollable));
    }

    /* The turn was shot on a lit backdrop rather than a flat colour: it runs
       about 24 levels brighter around the left quarter than at the right edge.
       A single fill is therefore wrong by roughly 12 at the extremes, which
       reads as a horizontal edge where the letterbox meets the frame — subtle,
       but on an area this large and this calm it is exactly the kind of thing
       that gets noticed. These stops are measured from the frames' own top
       rows, averaged across nine positions around the rotation, so the fill
       carries the same gradient the photograph does.

       Cached per width: the gradient only changes when the canvas is resized,
       and rebuilding one on every scrubbed frame is work for nothing.

       If the 360 is ever re-rendered, re-measure these. */
    bandFill(cw) {
      if (this.fillCache && this.fillCacheW === cw) return this.fillCache;
      const stops = [
        [0.000, '#B5C7D8'], [0.125, '#B8C9DA'], [0.250, '#B8CADA'],
        [0.375, '#B5C7D8'], [0.500, '#B5C5D6'], [0.625, '#B2C3D4'],
        [0.750, '#ACBECE'], [0.875, '#A6B8C9'], [1.000, '#A0B4C6']
      ];
      const g = this.ctx.createLinearGradient(0, 0, cw, 0);
      stops.forEach((s) => g.addColorStop(s[0], s[1]));
      this.fillCache = g;
      this.fillCacheW = cw;
      return g;
    }

    paint(index) {
      const img = this.frames[index];
      if (!img || !img.complete || !img.naturalWidth) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cw = this.canvas.clientWidth;
      const ch = this.canvas.clientHeight;
      if (!cw || !ch) return;

      if (this.canvas.width !== cw * dpr || this.canvas.height !== ch * dpr) {
        this.canvas.width = cw * dpr;
        this.canvas.height = ch * dpr;
      }

      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.ctx.fillStyle = this.bandFill(cw);
      this.ctx.fillRect(0, 0, cw, ch);

      // Contain, so the whole product is always visible.
      const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      const x = (cw - w) / 2;
      // On a phone the callout docks along the bottom, so the product is
      // seated high rather than centred and the two never overlap.
      const y = (ch - h) * (cw < 750 ? 0.26 : 0.5);

      // The bands either side of the contained frame are a flat fill, taken
      // from --walkaround-bg. That token has to match the backdrop the frames
      // were rendered against — measured at #ADBFD1 at the top edge and
      // #AEBFD0 at the bottom, one unit apart, so a single colour covers both
      // and the join reads as continuous.
      //
      // An earlier attempt stretched the frame's own edge pixels into the
      // bands instead, on the theory that copying from the source is exact by
      // construction. It is not: the bottom edge row is not background. It
      // runs through the product's contact shadow, which measures as dark as
      // #4D5967 mid-row on some frames, and stretching that single row down
      // several hundred pixels smeared a black column over the whole section.
      // Sampling near the seam looked perfect, which is why it shipped. Do not
      // reintroduce it without checking what the far end of the band looks
      // like, not just the join.
      this.box = { x: x, y: y, w: w, h: h };
      this.ctx.drawImage(img, x, y, w, h);
      this.painted = index;
    }

    measureCallout() {
      if (!this.el.callout || this.el.callout.hasAttribute('hidden')) return null;
      const c = this.el.callout.getBoundingClientRect();
      const s = this.stage.getBoundingClientRect();
      return { x: c.left - s.left, y: c.top - s.top, w: c.width, h: c.height };
    }

    /* Nearest callout to the current angle, with a 0–1 strength for the fade. */
    resolveSpot(frame) {
      let best = null;
      let strength = 0;
      for (let i = 0; i < this.spots.length; i++) {
        const spot = this.spots[i];
        const distance = Math.abs(frame - spot.frame);
        if (distance < spot.span) {
          const s = 1 - distance / spot.span;
          if (s > strength) { strength = s; best = spot; }
        }
      }
      return { spot: best, strength: strength };
    }

    tick() {
      this.measure();
      this.eased += (this.target - this.eased) * 0.14;

      const p = this.eased;
      const index = Math.min(this.count - 1, Math.max(0, Math.round(p * (this.count - 1))));
      if (index !== this.painted) this.paint(index);

      if (this.el.degrees) this.el.degrees.textContent = Math.round(p * 360) + '°';
      if (this.el.railFill) this.el.railFill.style.transform = 'scaleX(' + p + ')';
      if (this.el.hint) this.el.hint.style.opacity = p > 0.02 ? '0' : '1';

      const found = this.resolveSpot(index);
      this.renderCallout(found.spot, found.strength, index);

      this.raf = requestAnimationFrame(this.tick);
    }

    renderCallout(spot, strength, index) {
      const els = this.el;

      if (!spot) {
        if (this.activeId !== null) {
          this.activeId = null;
          if (els.callout) els.callout.setAttribute('hidden', '');
          if (els.marker) els.marker.style.opacity = '0';
          if (els.leader) els.leader.style.opacity = '0';
        }
        if (this.ticks) this.ticks.forEach((t) => t.classList.remove('is-on'));
        return;
      }

      const id = spot.index;
      if (id !== this.activeId) {
        this.activeId = id;
        if (els.callout) els.callout.removeAttribute('hidden');
        if (els.cIndex) els.cIndex.textContent = spot.index;
        if (els.cTitle) els.cTitle.textContent = spot.title;
        if (els.cBody) els.cBody.textContent = spot.body;
        if (els.cSpec) {
          els.cSpec.textContent = spot.spec;
          els.cSpec.style.display = spot.spec ? '' : 'none';
        }
        if (this.ticks) {
          this.ticks.forEach((t, i) => {
            t.classList.toggle('is-on', this.spots[i] === spot);
          });
        }
      }

      const opacity = Math.min(1, strength * 2.1);
      if (els.callout) els.callout.style.opacity = opacity;

      const ax = this.box.x + spot.x * this.box.w;
      const ay = this.box.y + spot.y * this.box.h;

      if (els.marker) {
        els.marker.style.opacity = opacity;
        els.marker.style.transform = 'translate(' + ax.toFixed(1) + 'px,' + ay.toFixed(1) + 'px)';
      }

      const rect = this.measureCallout();
      if (rect && els.leaderLine && this.stage.clientWidth >= 1000) {
        const railY = rect.y + rect.h + 22;
        const midX = rect.x + rect.w / 2;
        els.leaderLine.setAttribute(
          'points',
          ax + ',' + ay + ' ' + ax + ',' + railY + ' ' + midX + ',' + railY + ' ' + midX + ',' + (rect.y + rect.h)
        );
        els.leader.style.opacity = opacity;
      } else if (els.leader) {
        els.leader.style.opacity = '0';
      }
    }
  }

  define('lum-walkaround', LumWalkaround);

  /* ------------------------------------------------------------------------
     Product carousel
     One frame at a time, with its copy held in a column beside it.

     The scroll position is the state. There is no internal "current slide"
     that the DOM is then told to match — the index is read back out of
     scrollLeft, and every control works by scrolling. That means a finger
     flick, an arrow key, a dot and the autoplay timer all travel through the
     same path, and none of them can disagree with what is on screen. A
     carousel that keeps its own index alongside a native scroller has two
     sources of truth, and they drift the first time a swipe is interrupted.

     Timing is delegated to CSS. The progress bar's animation IS the wait:
     advancing happens on its animationend, pausing is one
     animation-play-state, and the bar therefore cannot lie about when the
     next slide is due. A setTimeout running next to a CSS animation is two
     clocks, and they separate the moment the tab is backgrounded.
     ---------------------------------------------------------------------- */
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-carousel-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.panels = Array.from(this.querySelectorAll('[data-carousel-panel]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.status = this.querySelector('[data-carousel-status]');
      this.toggleBtn = this.querySelector('[data-carousel-toggle]');
      this.toggleLabel = this.querySelector('[data-carousel-toggle-label]');

      // One slide is a photograph, not a carousel. Controls that cannot move
      // anything are worse than no controls, so leave them out of the tab
      // order entirely rather than shipping dead buttons.
      if (!this.scroller || this.slides.length < 2) {
        const controls = this.querySelector('.modes__controls');
        if (controls) controls.hidden = true;
        return;
      }

      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 6000;

      // Reduced motion turns autoplay off outright rather than merely slowing
      // it. Content that moves without being asked is the exact thing that
      // preference is expressing a position on.
      this.autoWanted = this.dataset.autoplay !== 'false' && !prefersReducedMotion();

      this.userPaused = false;
      this.hovered = false;
      this.focusWithin = false;
      this.inView = true;
      this.dragging = false;

      this.bind();
      this.observe();

      // Stated rather than inherited. The button ships hidden from Liquid, so
      // "unhide when autoplay is on" happens to work — until the element is
      // cloned or re-rendered from a DOM that has already been through this,
      // at which point a stop button appears for a carousel with nothing to
      // stop. The control's visibility is a function of autoWanted, so say so.
      if (this.toggleBtn) this.toggleBtn.hidden = !this.autoWanted;
      if (!this.autoWanted) this.setAttribute('data-auto', 'off');

      this.setIndex(0, false);
      this.updateAuto();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    bind() {
      // Scroll is read, never written to directly: rAF-coalesced so a
      // momentum flick costs one measurement per frame rather than one per
      // scroll event, of which a phone fires a great many.
      let ticking = false;
      this.scroller.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          if (!this.dragging) this.syncFromScroll();
        });
      }, { passive: true });

      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.step(1));

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i, true));
      });

      this.toggleBtn?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        if (this.toggleLabel) this.toggleLabel.textContent = this.userPaused ? 'Play' : 'Pause';
        this.toggleBtn.setAttribute(
          'aria-label',
          this.userPaused ? 'Play the mode carousel' : 'Pause the mode carousel'
        );
        this.updateAuto();
      });

      // Arrow keys move a whole slide. Native keyboard scrolling on a snap
      // container travels by a scroll step and then snaps back, which reads
      // as the page refusing the keypress.
      this.scroller.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        this.step(e.key === 'ArrowRight' ? 1 : -1);
      });

      // Hover and focus hold the timer without cancelling it — the bar
      // freezes and picks up where it stopped. Someone reading the copy
      // beside the frame should not have it swapped out from under them.
      this.addEventListener('pointerenter', () => { this.hovered = true; this.updateAuto(); });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.updateAuto(); });
      this.addEventListener('focusin', () => { this.focusWithin = true; this.updateAuto(); });
      this.addEventListener('focusout', (e) => {
        if (this.contains(e.relatedTarget)) return;
        this.focusWithin = false;
        this.updateAuto();
      });

      this.onVisibility = () => this.updateAuto();
      document.addEventListener('visibilitychange', this.onVisibility);

      // Advancing rides the progress bar's own animation. Guarded anyway:
      // the reduced-motion block in base.css collapses every animation to
      // 0.01ms, and an unguarded handler would sprint the whole strip.
      this.addEventListener('animationend', (e) => {
        if (e.animationName !== 'modes-fill') return;
        if (!this.autoWanted || this.userPaused) return;
        this.step(1);
      });

      this.bindDrag();
    }

    /* Mouse drag only. Touch already has a better version of this natively,
       and hijacking it costs the momentum and the rubber band. */
    bindDrag() {
      this.scroller.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        this.dragging = true;
        this.dragX = e.clientX;
        this.dragScroll = this.scroller.scrollLeft;
        this.scroller.classList.add('is-dragging');
        this.scroller.setPointerCapture(e.pointerId);
      });

      this.scroller.addEventListener('pointermove', (e) => {
        if (!this.dragging) return;
        this.scroller.scrollLeft = this.dragScroll - (e.clientX - this.dragX);
      });

      const end = (e) => {
        if (!this.dragging) return;
        this.dragging = false;
        this.scroller.classList.remove('is-dragging');
        if (e.pointerId != null && this.scroller.hasPointerCapture?.(e.pointerId)) {
          this.scroller.releasePointerCapture(e.pointerId);
        }
        // Snapping was suspended for the drag, so re-engaging it is not
        // enough on its own — land the nearest slide explicitly.
        this.goTo(this.nearest(), true);
      };

      this.scroller.addEventListener('pointerup', end);
      this.scroller.addEventListener('pointercancel', end);
    }

    observe() {
      if (!('IntersectionObserver' in window)) return;
      // A carousel advancing off-screen is spending a wake-up per slide to
      // move something nobody is looking at, and it means the reader arrives
      // partway through the sequence rather than at its first frame.
      this.io = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        this.updateAuto();
      }, { threshold: 0.35 });
      this.io.observe(this);
    }

    /* Position of a slide within the scroller, measured rather than assumed.
       offsetLeft depends on which ancestor happens to be positioned; this
       does not. */
    offsetOf(slide) {
      return slide.getBoundingClientRect().left
        - this.scroller.getBoundingClientRect().left
        + this.scroller.scrollLeft;
    }

    nearest() {
      const x = this.scroller.scrollLeft;
      let best = 0;
      let bestGap = Infinity;
      this.slides.forEach((slide, i) => {
        const gap = Math.abs(this.offsetOf(slide) - x);
        if (gap < bestGap) { bestGap = gap; best = i; }
      });
      return best;
    }

    syncFromScroll() {
      const i = this.nearest();
      if (i !== this.index) this.setIndex(i, false);
    }

    step(dir) {
      const next = (this.index + dir + this.slides.length) % this.slides.length;
      this.goTo(next, true);
    }

    goTo(i, user) {
      const slide = this.slides[i];
      if (!slide) return;
      this.scroller.scrollTo({
        left: this.offsetOf(slide),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
      this.setIndex(i, user);
    }

    setIndex(i, user) {
      this.index = i;

      this.panels.forEach((panel, n) => {
        panel.toggleAttribute('data-active', n === i);
      });

      this.dots.forEach((dot, n) => {
        if (n === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      // Announced only when a person moved it. Narrating an autoplay loop
      // into a screen reader is noise nobody asked for.
      if (user && this.status) {
        const title = this.panels[i]?.querySelector('.modes__title')?.textContent?.trim();
        this.status.textContent = (i + 1) + ' of ' + this.slides.length + (title ? ': ' + title : '');
      }
    }

    updateAuto() {
      if (!this.autoWanted) return;
      const running =
        !this.userPaused &&
        !this.hovered &&
        !this.focusWithin &&
        !this.dragging &&
        this.inView &&
        !document.hidden;
      this.toggleAttribute('data-paused', !running);
    }
  }

  define('product-carousel', ProductCarousel);

  const boot = () => {
    Reveal.init();
    Marquee.init();
    Spotlight.init();
    Parallax.init();
    HeaderTheme.init();
    BgVideo.init();
    MorphMedia.init();
    // Runs last: it depends on Reveal.observer existing for headlines that
    // start below the fold.
    SplitLines.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-scan newly injected sections so reveals still fire.
  document.addEventListener('shopify:section:load', (e) => {
    Reveal.scan(e.target);
    Marquee.init(e.target);
  });
})();
